import {
  PropsWithChildren,
  useState,
  ForwardRefExoticComponent,
  PropsWithoutRef,
  RefAttributes,
  forwardRef,
} from 'react';
import { noop } from 'lodash';

import { IonCard } from '@ionic/react';
import classNames from 'classnames';
import { useTheme } from 'styled-components';

import { ThemePaletteColor } from '../../types';

import { CardIcon } from './CardIcon/CardIcon';
import { CardState } from './Card.types';
import { CardHeader } from './CardHeader/CardHeader';
import { CardTitle } from './CardTitle/CardTitle';
import { CardContent } from './CardContent/CardContent';
import { CardFooter } from './CardFooter/CardFooter';
import { CardThumb } from './CardThumb/CardThumb';
import { CardAvatar } from './CardAvatar/CardAvatar';
import { CardContext } from './Card.context';

import styles from './Card.module.scss';

type CardProps = PropsWithChildren<{
  divider?: boolean;
  className?: string;
  onClick?: () => void;
  variant?: 'flat' | 'default';
  background?: ThemePaletteColor;
}>;

type CardComponent = ForwardRefExoticComponent<
  PropsWithoutRef<CardProps> & RefAttributes<HTMLIonCardElement>
> & {
  Icon: typeof CardIcon;
  Header: typeof CardHeader;
  Title: typeof CardTitle;
  Content: typeof CardContent;
  Footer: typeof CardFooter;
  Thumb: typeof CardThumb;
  Avatar: typeof CardAvatar;
};

export const Card = forwardRef<HTMLIonCardElement, CardProps>(
  (
    {
      children,
      className,
      onClick = noop,
      divider = false,
      variant = 'default',
      background = 'white100',
    },
    ref
  ) => {
    const [{ icon, avatar }, setState] = useState<CardState>({});

    const { palette } = useTheme();

    return (
      <CardContext.Provider value={{ setState }}>
        <IonCard
          ref={ref}
          onClick={onClick}
          style={{
            '--background': palette[background],
            '--avatar': avatar ? `${avatar}px` : undefined,
          }}
          className={classNames(
            styles.root,
            styles[variant],
            {
              [styles.withAvatar]: !!avatar,
              [styles.withSmIcon]: icon?.size === 'sm',
              [styles.withMdIcon]: icon?.size === 'md',
              [styles.clickable]: onClick !== noop,
              [styles.divider]: divider,
            },
            className
          )}
        >
          {children}
        </IonCard>
      </CardContext.Provider>
    );
  }
) as CardComponent;

Card.Icon = CardIcon;
Card.Header = CardHeader;
Card.Title = CardTitle;
Card.Content = CardContent;
Card.Footer = CardFooter;
Card.Thumb = CardThumb;
Card.Avatar = CardAvatar;
